import React, { useEffect } from "react";
import { Input, Text, Select } from "components-lib";
// import { getPrescriptionslatestbyappt } from "../../../ApiCalls/prescriptionApis"; // (not used currently)

/**
 * PastHistorySection - Clinical Past History Management Component
 *
 * This component provides a comprehensive interface for managing patient clinical
 * past history data within the consultation workflow. It allows healthcare providers
 * to add, display, and manage historical clinical information for better patient care.
 *
 * Key Features:
 * - Add new clinical history entries with validation
 * - Display existing history entries in an organized format
 * - Delete individual history entries
 * - Integration with appointment-specific data
 * - Real-time state management for seamless UX
 *
 * Component Structure:
 * - Input field for entering new history entries
 * - Add button with validation (disabled when empty)
 * - Display container showing all added histories
 * - Individual delete buttons for each history entry
 * - Responsive table-based layout
 *
 * Props:
 * @param {Array} PastHistoryData - Array of existing clinical history entries
 * @param {Function} setPastHistoryData - State setter for updating history data
 * @param {Object} newPastHistory - Current input state for new history entry
 * @param {Function} setNewPastHistory - State setter for new history input
 * @param {string|number} appointmentId - Current appointment identifier
 *
 * Data Model (aligned with complaints section):
 *  Each past history entry is stored as:
 *  {
 *    pasthistory_no: 'H-001', // sequential id for display/order
 *    pasthistory: string,      // main text (was `history` previously)
 *    severity: string,        // optional severity level
 *    duration: string,        // human readable duration e.g. '4 months'
 *    date: 'YYYY-MM-DD'       // start/onset date
 *  }
 *  NOTE: We keep backward compatibility by also accepting objects with
 *  { history_no, history } when merging template / API data and normalize them.
 *
 * Numbering scheme mirrors complaints: H-001, H-002 ...
 *
 * State Management:
 * - Generates pasthistory_no sequentially based on current array length
 * - Dedupe based on lowercase pasthistory text (ignoring empty string)
 * - Allows inline add / delete operations
 *
 * Usage:
 * ```jsx
 * <PastHistorySection
 *   PastHistoryData={historyEntries}
 *   setPastHistoryData={setHistoryEntries}
 *   newPastHistory={currentInput}
 *   setNewPastHistory={setCurrentInput}
 *   appointmentId={appointmentId}
 * />
 * ```
 *
 * Integration:
 * - Part of the consultation form workflow
 * - Data persists with appointment/prescription data
 * - Styled to match other form sections consistently
 *
 * @component
 * @since 1.0.0
 * @author iceking-1912
 */
const PastHistorySection = ({
  PastHistoryData,
  PastpatientSpecifcData,
  setPastHistoryData,
  newPastHistory,
  setNewPastHistory,
  appointmentId,
}) => {
  useEffect(() => {
    console.log(
      "🔴 PastpatientSpecifcData Recived at Past History",
      JSON.stringify(PastpatientSpecifcData)
    );
  }, [PastpatientSpecifcData]);

  // Helper: generate next history numbers (both forms)
  const generateHistoryNos = (index) => {
    const base = String(index + 1).padStart(3, "0");
    return {
      pasthistory_no: `H-${base}`, // dashed variant (new)
      history_no: `H${base}`, // non-dashed legacy variant
    };
  };

  // Normalize legacy data entries (history_no/history) into new structure
  const normalizeEntry = (item, index) => {
    if (!item) return null;
    // If already in new shape (pasthistory field present) return with ensured pasthistory_no
    // Determine base text & existing numbers
    const text = item.pasthistory || item.history || "";
    let existingDash = item.pasthistory_no || "";
    let existingPlain = item.history_no || "";

    // Normalize numbers if provided only in one style
    if (!existingDash && existingPlain) {
      // Convert H### to H-###
      if (/^H\d{3}$/i.test(existingPlain)) {
        existingDash = existingPlain.replace(/^H(\d{3})$/i, "H-$1");
      }
    }
    if (!existingPlain && existingDash) {
      // Convert H-### to H###
      if (/^H-\d{3}$/i.test(existingDash)) {
        existingPlain = existingDash.replace(/^H-(\d{3})$/i, "H$1");
      }
    }
    // Generate if still missing
    if (!existingDash || !existingPlain) {
      const gen = generateHistoryNos(index);
      existingDash = existingDash || gen.pasthistory_no;
      existingPlain = existingPlain || gen.history_no;
    }

    return {
      pasthistory_no: existingDash,
      history_no: existingPlain,
      pasthistory: text,
      history: text, // legacy mirror
      severity: item.severity || "",
      duration: item.duration || "",
      date: item.date || "",
    };
  };

  // Ensure existing list is normalized once on mount / appointment change
  useEffect(() => {
    if (!Array.isArray(PastHistoryData) || PastHistoryData.__normalized) return;
    const normalized = PastHistoryData.map(normalizeEntry).filter(Boolean);
    normalized.__normalized = true; // mark to avoid re-normalization loops
    setPastHistoryData(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  const handleAddPastHistory = () => {
    const text = (
      newPastHistory?.pasthistory ||
      newPastHistory?.history ||
      ""
    ).trim();
    if (!text) return;

    const lower = text.toLowerCase();
    const exists = (PastHistoryData || []).some(
      (p) => (p.pasthistory || p.history || "").toLowerCase() === lower
    );
    if (exists) {
      setNewPastHistory({
        pasthistory: "",
        severity: "",
        duration: "",
        date: "",
      });
      return;
    }

    const next = normalizeEntry(
      {
        pasthistory: text,
        history: text,
        duration: newPastHistory.duration || "",
        date: newPastHistory.date || "",
      },
      PastHistoryData.length
    );

    setPastHistoryData([...(PastHistoryData || []), next]);
    setNewPastHistory({
      pasthistory: "",
      severity: "",
      duration: "",
      date: "",
    });
  };

  const handleDeletePastHistory = (index) => {
    setPastHistoryData((PastHistoryData || []).filter((_, i) => i !== index));
  };
  useEffect(() => {
    if (!PastpatientSpecifcData) return;

    // support API shape where payload is wrapped in `.data`
    const payload = PastpatientSpecifcData.data || PastpatientSpecifcData;

    // possible keys where past history may live
    const raw =
      payload.past_history ||
      payload.pasthistory ||
      payload.past_histories ||
      payload.history ||
      payload.PastHistoryData ||
      null;

    if (!raw) return;

    // normalize raw into array of items (strings or objects)
    const items = [];
    if (Array.isArray(raw)) {
      items.push(...raw);
    } else if (typeof raw === "string") {
      raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((s) => items.push(s));
    } else if (raw && typeof raw === "object") {
      // single object possibly containing fields
      items.push(raw);
    } else {
      return;
    }

    // merge into state avoiding duplicates (case-insensitive)
    setPastHistoryData((prev = []) => {
      const lowerSet = new Set(
        (prev || []).map((p) =>
          (p.pasthistory || p.history || "").toLowerCase()
        )
      );
      const next = [...prev];

      items.forEach((it) => {
        let entryObj;
        if (typeof it === "string") {
          entryObj = { pasthistory: it };
        } else if (it && typeof it === "object") {
          entryObj = it;
        } else {
          return;
        }

        const text = (entryObj.pasthistory || entryObj.history || "").trim();
        if (!text) return;

        if (lowerSet.has(text.toLowerCase())) return;

        // generate normalized entry using existing helper
        const normalized = normalizeEntry(entryObj, next.length);
        if (normalized) {
          next.push(normalized);
          lowerSet.add(text.toLowerCase());
        }
      });

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PastpatientSpecifcData, appointmentId]);

  return (
    <tr>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        <Text fontWeight="bold" style={{ fontSize: "16px" }}>
          Past History:
        </Text>
      </td>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        {/* Input Row - mimic complaints table style */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#ffffff" }}>
                <th style={thStyle}>Past History</th>
                <th style={thStyle}>Duration</th>
                {/* <th style={thStyle}>Start Date</th> */}
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {(PastHistoryData || []).map((item, idx) => (
                <tr key={idx}>
                  <td style={tdStyle}>{item.pasthistory || item.history}</td>
                  <td style={tdStyle}>{item.duration || "-"}</td>
                  {/* <td style={tdStyle}>{item.date || "-"}</td> */}
                  <td style={tdStyle}>
                    <button
                      style={deleteBtnStyle}
                      
                      onClick={() => handleDeletePastHistory(idx)}
                      aria-label="Delete past history entry"
                    >
                      X
                    </button>
                  </td>
                </tr>
              ))}
              {/* Input row */}
              <tr>
                <td style={tdStyle}>
                  <Input
                    value={
                      newPastHistory.pasthistory || newPastHistory.history || ""
                    }
                    onChange={(e) =>
                      setNewPastHistory({
                        ...newPastHistory,
                        pasthistory: e.target.value,
                      })
                    }
                    placeholder="Enter past history..."
                    size="md"
                  />
                </td>
                <td style={tdStyle}>
                  <Select
                    value={newPastHistory.duration || ""}
                    onChange={(e) =>
                      setNewPastHistory({
                        ...newPastHistory,
                        duration: e.target.value,
                      })
                    }
                    placeholder="Duration"
                    size="md"
                  >
                    <option value="1-2 hour">1-2 hour</option>
                    <option value="6 hours">6 hours</option>
                    <option value="12 hours">12 hours</option>
                    <option value="1 day">1 day</option>
                    <option value="2 days">2 days</option>
                    <option value="3 days">3 days</option>
                    <option value="1 week">1 week</option>
                    <option value="2 weeks">2 weeks</option>
                    <option value="3 weeks">3 weeks</option>
                    <option value="1 month">1 month</option>
                    <option value="2 months">2 months</option>
                    <option value="3 months">3 months</option>
                    <option value="4 months">4 months</option>
                    <option value="5 months">5 months</option>
                    <option value="6 months">6 months</option>
                    <option value=">6 months">More than 6 months</option>
                  </Select>
                </td>
                {/* <td style={tdStyle}>
                  <Input
                    type="
                    value={newPastHistory.date || ""}
                    onChange={(e) =>
                      setNewPastHistory({
                        ...newPastHistory,
                        date: e.target.value,
                      })
                    }
                    size="md"
                  />
                </td> */}
                <td style={tdStyle}>
                  <button
                    style={addBtnStyle}
                    onClick={handleAddPastHistory}
                    disabled={
                      !(
                        newPastHistory.pasthistory ||
                        newPastHistory.history ||
                        ""
                      ).trim()
                    }
                  >
                    Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
};

// Simple reusable style objects (kept outside component to avoid re-creation)
const thStyle = {
  border: "1px solid #E2E8F0",
  padding: "6px 8px",
  fontSize: "15px",
  fontWeight: 600,
  textAlign: "left",
  whiteSpace: "nowrap",
};
const tdStyle = {
  border: "1px solid #E2E8F0",
  padding: "4px 6px",
  fontSize: "15px",
  verticalAlign: "middle",
};
const addBtnStyle = {
  backgroundColor: "#16A34A",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  padding: "4px 10px",
  fontSize: "12px",
  cursor: "pointer",
};
const deleteBtnStyle = {
  backgroundColor: "#e2e8f0",
  color: "red",
  fontWeight: "bold",
  border: "none",
  borderRadius: "50%",
  width: 22,
  height: 22,
  fontSize: "12px",
  lineHeight: "22px",
  cursor: "pointer",
};

export default PastHistorySection;
